// RM2 : le texte et les aperçus restent dans cet écran, sans stockage ni cache partagé.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { identifiantRetourValide, nouvelleCle } from '../api/client';
import type { DepotRetour, DetailRetour, EtatRetour, EvenementRetour } from '../api/types';
import { Chargement, Erreur } from '../composants/communs';
import { useEkera } from '../contexte';
import { lienVers, naviguer } from '../routeur';

const ETATS: Record<EtatRetour, string> = {
  recu: 'Reçu', proposition: 'À relire', anonymisation_validee: 'Version anonymisée validée',
  retenu: 'Retenu', ecarte: 'Écarté', livre: 'Livré', retire: 'Retiré',
};
const RAISONS: Record<string, string> = {
  deja_disponible: 'Déjà disponible', doublon: 'Doublon', hors_perimetre: 'Hors périmètre',
  contraire_aux_regles: 'Contraire aux règles', non_reproductible: 'Non reproductible',
  non_retenu_pour_l_instant: 'Non retenu pour l’instant', autre: 'Autre raison',
};
const CLASSES: Record<string, string> = {
  fonction_manquante: 'Fonction manquante', defaut: 'Défaut', calcul_conteste: 'Calcul contesté',
  lecture: 'Lecture', ergonomie: 'Ergonomie', autre: 'Autre',
};
const MODES = { regles: 'Règles', modele: 'Modèle de langage', auteur: 'Texte corrigé par vous' };
const date = (instant: string) => new Date(instant).toLocaleString('fr-FR', { timeZone: 'UTC' });

/** Seuls des contextes fixes d'écran sont acceptés ; aucun texte ou objet de carte n'est recopié. */
function origine(params: URLSearchParams): Pick<DepotRetour, 'canal' | 'type_objet' | 'ecran'> {
  const depuis = params.get('depuis');
  if (depuis === 'console') return { canal: 'console', type_objet: 'ecran', ecran: 'interroger' };
  if (depuis === 'chiffre-console') return { canal: 'chiffre', type_objet: 'ecran', ecran: 'interroger' };
  if (depuis === 'chiffre') return { canal: 'chiffre', type_objet: 'ecran', ecran: 'constats' };
  if (depuis === 'constat') return { canal: 'constat', type_objet: 'ecran', ecran: 'constats' };
  return { canal: 'ecran', type_objet: 'ecran', ecran: 'retours' };
}

export function EcranRetours({ params }: { params: URLSearchParams }) {
  const { moi } = useEkera();
  if (moi.anonyme || moi.compte_service || !moi.interactif)
    return <p role="alert">Les retours sont réservés à une personne connectée par l’application web.</p>;
  const id = params.get('retour');
  if (id !== null && !identifiantRetourValide(id)) return <p role="alert">Retour introuvable</p>;
  // Un changement d'organisation ou d'auteur oublie immédiatement toute saisie et toute réponse en vol.
  return <Retours key={JSON.stringify([moi.client, moi.emetteur, moi.sujet])} params={params} />;
}

function Retours({ params }: { params: URLSearchParams }) {
  const { client } = useEkera();
  const contexte = origine(params);
  const [texte, setTexte] = useState('');
  const tentative = useRef<{ corps: string; cle: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [liste, setListe] = useState<Awaited<ReturnType<typeof client.retours>> | null>(null);
  const [erreurListe, setErreurListe] = useState<unknown>(null);
  const [curseur, setCurseur] = useState<string>();
  const [revision, setRevision] = useState(0);
  const vivant = useRef(true);
  useEffect(() => { vivant.current = true; return () => { vivant.current = false; }; }, []);
  useEffect(() => {
    let actif = true;
    setListe(null);
    setErreurListe(null);
    void client.retours(curseur).then(
      (r) => { if (actif) setListe(r); },
      (e: unknown) => { if (actif) setErreurListe(e); },
    );
    return () => { actif = false; };
  }, [client, curseur, revision]);
  const rafraichir = () => setRevision((n) => n + 1);

  async function deposer(e: FormEvent) {
    e.preventDefault();
    if (enCours || !texte.trim()) return;
    const corps = { ...contexte, texte };
    const serialise = JSON.stringify(corps);
    if (tentative.current?.corps !== serialise) tentative.current = { corps: serialise, cle: nouvelleCle() };
    setEnCours(true);
    setErreur(null);
    try {
      const r = await client.deposerRetour(corps, tentative.current.cle);
      if (!vivant.current) return;
      setTexte('');
      tentative.current = null;
      setCurseur(undefined);
      rafraichir();
      naviguer('retours', { retour: r.id });
    } catch (err) {
      if (vivant.current) setErreur(err);
    } finally {
      if (vivant.current) setEnCours(false);
    }
  }

  const id = params.get('retour');
  return <div className="pile">
    <p className="sous-titre">Dites ce qui manque ou ce qui vous semble à corriger. Une reformulation et une version anonymisée vous seront proposées pour relecture. Aucun chiffre ni élément de la carte n’est modifié.</p>
    <p className="note">Le partage vers la boucle d’amélioration n’est pas encore disponible. Valider la version anonymisée ne l’envoie pas hors de votre entreprise.</p>
    <section className="panneau pile" aria-labelledby="t-depot-retour">
      <h2 id="t-depot-retour">Nouveau retour</h2>
      {contexte.ecran !== 'retours' && <p role="note" data-testid="contexte-retour">
        Contexte affiché seulement : {contexte.ecran === 'interroger' ? 'console d’interrogation' : 'constats et scénarios'}
        {contexte.canal === 'chiffre' ? ', contestation d’un chiffre' : contexte.canal === 'constat' ? ', retour sur un constat' : ''}.
        {' '}Seul l’écran d’origine est rattaché ; le constat ou le chiffre précis ne peut pas encore l’être. Décrivez votre retour avec vos mots.
      </p>}
      <form className="pile" onSubmit={(e) => void deposer(e)} autoComplete="off">
        <div className="champ">
          <label htmlFor="texte-retour">Votre retour</label>
          <textarea id="texte-retour" rows={4} maxLength={4000} value={texte} disabled={enCours}
            onChange={(e) => setTexte(e.target.value)} aria-describedby="aide-retour" />
          <p id="aide-retour" className="aide">Ne collez aucun secret. Le texte est enregistré pour votre relecture ; aucune donnée de la carte n’est ajoutée automatiquement.</p>
        </div>
        <button className="btn principal" type="submit" disabled={enCours || !texte.trim()}>{enCours ? 'Enregistrement…' : 'Enregistrer le retour'}</button>
      </form>
      {erreur != null && <Erreur erreur={erreur} titre="Le retour n’a pas pu être enregistré. Votre saisie est conservée." />}
    </section>
    <section className="panneau pile" aria-labelledby="t-liste-retours">
      <div className="panneau-tete"><h2 id="t-liste-retours">Vos retours</h2><button className="btn petit" onClick={rafraichir}>Actualiser la liste</button></div>
      <p className="note">Vos retours pour l’organisation active, tels que l’API les rend. Les retours des autres personnes ne sont pas visibles.</p>
      {erreurListe != null ? <Erreur erreur={erreurListe} /> : !liste ? <Chargement quoi="des retours" /> : <>
        {liste.retours.length === 0 ? <p>Aucun retour sur cette page.</p> : <ul>{liste.retours.map((r) => <li key={r.id}>
          <a href={lienVers('retours', { retour: r.id })} aria-current={id === r.id ? 'true' : undefined}>Retour du {date(r.cree_le)} (UTC)</a>
          {' — '}{ETATS[r.etat] ?? r.etat}
        </li>)}</ul>}
        <div className="ligne">
          {curseur && <button className="btn petit" onClick={() => setCurseur(undefined)}>Première page</button>}
          {liste.suivant && <button className="btn petit" onClick={() => setCurseur(liste.suivant!)}>Page suivante</button>}
        </div>
      </>}
    </section>
    {id && <FicheRetour key={id} id={id} surChangement={rafraichir} />}
  </div>;
}

function derniereProposition(vue: DetailRetour) {
  return vue.evenements.filter((e) => e.nature === 'proposition').at(-1);
}

function FicheRetour({ id, surChangement }: { id: string; surChangement: () => void }) {
  const { client } = useEkera();
  const [vue, setVue] = useState<DetailRetour | null>(null);
  const [correction, setCorrection] = useState('');
  const [relu, setRelu] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [revision, setRevision] = useState(0);
  const vivant = useRef(true);
  useEffect(() => { vivant.current = true; return () => { vivant.current = false; }; }, []);
  function montrer(r: DetailRetour) {
    setVue(r);
    setCorrection(derniereProposition(r)?.contenu?.version_anonymisee ?? '');
    setRelu(false);
  }
  useEffect(() => {
    let actif = true;
    setVue(null);
    setRelu(false);
    setErreur(null);
    void client.retour(id).then((r) => { if (actif) montrer(r); }, (e: unknown) => { if (actif) setErreur(e); });
    return () => { actif = false; };
  }, [client, id, revision]);

  async function agir(action: 'proposer' | 'corriger' | 'valider') {
    if (enCours || !vue) return;
    const p = derniereProposition(vue);
    const version = p?.contenu?.version_anonymisee;
    if (action === 'valider' && (!vue.avertissement_validation?.trim() || !relu || !version || correction !== version || !p?.empreinte_texte)) return;
    setEnCours(true);
    setErreur(null);
    setRelu(false);
    try {
      const r = action === 'valider'
        ? await client.validerRetour(id, { version_anonymisee: version!, empreinte: p!.empreinte_texte!, valide: true })
        : await client.proposerRetour(id, action === 'corriger' ? { texte_corrige: correction } : {});
      if (vivant.current) { montrer(r); surChangement(); }
    } catch (err) {
      if (vivant.current) setErreur(err);
    } finally {
      if (vivant.current) setEnCours(false);
    }
  }

  const propositions = vue?.evenements.filter((e) => e.nature === 'proposition') ?? [];
  const p = propositions.at(-1);
  const version = p?.contenu?.version_anonymisee;
  const termine = vue?.evenements.some((e) => ['anonymisation_validee', 'retenu', 'ecarte', 'livre', 'retire'].includes(e.nature));
  const modifie = correction !== version;
  const avertissementPresent = Boolean(vue?.avertissement_validation?.trim());
  return <section className="panneau pile" aria-labelledby="t-detail-retour">
    <div className="panneau-tete"><h2 id="t-detail-retour">Détail du retour</h2><button className="btn petit" disabled={enCours} onClick={() => setRevision((n) => n + 1)}>Recharger le retour</button></div>
    {erreur != null && <Erreur erreur={erreur} titre="L’API n’a pas pu terminer cette action." />}
    {!vue ? erreur == null && <Chargement quoi="du retour" /> : <>
      <h3>Votre texte enregistré</h3><p className="retour-texte">{vue.contenu.texte}</p>
      <h3>Suivi du retour</h3><ol>{vue.evenements.map((e) => <li key={e.numero}>
        <span>{ETATS[e.nature] ?? e.nature} — {date(e.instant)} (UTC)</span>
        {e.nature === 'ecarte' && <p>Raison : {e.raison ? RAISONS[e.raison] ?? e.raison : 'Non précisée par l’API'}{e.texte_ekera ? ` — ${e.texte_ekera}` : ''}</p>}
        {e.version_publique && <p>Version livrée : {e.version_publique}</p>}
        {e.nature === 'anonymisation_validee' && <p className="retour-texte">{e.contenu?.version_anonymisee}</p>}
      </li>)}</ol>
      {p && <Apercu proposition={p} />}
      {!termine && <>
        {!p && <button className="btn" disabled={enCours} onClick={() => void agir('proposer')}>Préparer une proposition à relire</button>}
        {p && <>
          <label className="champ" htmlFor="correction-retour">Corriger le texte proposé
            <textarea id="correction-retour" rows={4} maxLength={4000} value={correction} disabled={enCours}
              onChange={(e) => { setCorrection(e.target.value); setRelu(false); }} />
          </label>
          <p className="note">La correction sert à proposer une nouvelle reformulation et une version anonymisée. Relisez le résultat des filtres avant de le valider. Trois propositions au plus par retour.</p>
          <button className="btn" disabled={enCours || !correction.trim() || !modifie || propositions.length >= 3} onClick={() => void agir('corriger')}>Proposer ma correction</button>
          {propositions.length >= 3 && <>
            <p role="note">Le plafond de trois propositions est atteint. Vous pouvez revenir au texte du dernier aperçu pour le relire et le valider.</p>
            <button className="btn" disabled={enCours || !version} onClick={() => { setCorrection(version!); setRelu(false); }}>Revenir au texte proposé</button>
          </>}
          <p role="note">{avertissementPresent ? vue.avertissement_validation : 'L’avertissement de validation est indisponible. Rechargez le retour avant de valider.'}</p>
          <label className="case"><input type="checkbox" checked={relu} disabled={enCours || !avertissementPresent || modifie || !version}
            onChange={(e) => setRelu(e.target.checked)} />J’ai relu la reformulation, la version anonymisée et l’avertissement.</label>
          <button className="btn principal" disabled={enCours || !avertissementPresent || !relu || modifie || !version || !p.empreinte_texte} onClick={() => void agir('valider')}>Valider cette version anonymisée</button>
        </>}
      </>}
    </>}
  </section>;
}

function Apercu({ proposition: p }: { proposition: EvenementRetour }) {
  return <div className="pile" aria-label="Proposition à relire">
    <p className="note">Proposition à vérifier, sans valeur de fait établi. Origine : {p.mode ? MODES[p.mode] : 'Non précisée par l’API'}.
      {p.classe && <> Classe proposée : {CLASSES[p.classe] ?? p.classe}.</>}</p>
    <h3>Reformulation proposée</h3><p className="retour-texte">{p.contenu?.reformulation}</p>
    <h3>Version anonymisée proposée</h3><p className="retour-texte" data-testid="version-anonymisee">{p.contenu?.version_anonymisee}</p>
  </div>;
}
